// src/hooks/useStoneWorklistNav.ts
// Next-case navigation for the stone-review worklist, mirroring the reviewer's
// ReviewerControlBar.goToNextCase. Resolves the ordered study list from
// `?cohort=` (direct) or `?collection=` (→ its cohort, filtered to the
// collection's study_uids), finds the current study, and navigates to the next
// one preserving the URL param. Advance is pure client-side index nav — the
// reviewer's "next unreviewed" gating waits on server-tracked review status.

import { useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useReaderCohort } from "./useStudyQueries";
import { useCollections } from "./useCollections";
import { readerCaseId } from "../utils/readerCaseId";

export function useStoneWorklistNav(studyUid: string): { hasNext: boolean; goToNext: () => void } {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const cohortParam = searchParams.get("cohort");
  const collectionParam = searchParams.get("collection");

  const { collections } = useCollections();
  const collection = collectionParam ? collections.get(collectionParam) : undefined;
  const cohortId = cohortParam ?? collection?.cohortId ?? null;
  const { data: cohort } = useReaderCohort(token, cohortId);

  const ordered = useMemo(() => {
    const all = cohort?.studies ?? [];
    return collection ? all.filter((s) => collection.studyUids.has(s.study_uid)) : all;
  }, [cohort, collection]);

  const idx = ordered.findIndex((s) => s.study_uid === studyUid);
  const next = idx >= 0 && idx < ordered.length - 1 ? ordered[idx + 1] : null;

  const suffix = collectionParam
    ? `?collection=${encodeURIComponent(collectionParam)}`
    : cohortParam
      ? `?cohort=${encodeURIComponent(cohortParam)}`
      : "";

  return {
    hasNext: next != null,
    goToNext: () => {
      if (next) navigate(`/case-viewer/${readerCaseId(next)}${suffix}`);
    },
  };
}
